import React, { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { XCircle, RefreshCw, Home, ShoppingCart, AlertTriangle, Phone, Mail } from 'lucide-react'

function PaymentFailed() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  
  // Get payment details from URL parameters if available
  const orderNumber = searchParams.get('order_number')
  const amount = searchParams.get('amount')
  const reason = searchParams.get('reason')
  const transactionId = searchParams.get('transaction_id')

  useEffect(() => {
    // Auto-redirect after 5 minutes for security
    const timeout = setTimeout(() => {
      navigate('/')
    }, 300000) // 5 minutes

    return () => clearTimeout(timeout)
  }, [navigate])

  const handleRetryPayment = () => {
    if (orderNumber) {
      // Navigate to order details or checkout with the order number
      navigate(`/my-orders?retry=${orderNumber}`)
    } else {
      // Navigate to cart if no order number available
      navigate('/cart')
    }
  }

  const handleGoHome = () => {
    navigate('/')
  }

  const handleContactSupport = () => {
    // You can implement a support chat or redirect to contact page
    navigate('/contact-support')
  }

  const handleViewOrders = () => {
    navigate('/my-orders')
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-50 flex items-center justify-center px-4 py-8">
      <div className="max-w-2xl w-full">
        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 text-center">
          {/* Error Icon */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center">
                <XCircle className="w-16 h-16 text-red-500" />
              </div>
              <div className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>

          {/* Main Message */}
          <h1 className="text-3xl font-bold text-gray-800 mb-4">
            Payment Failed
          </h1>
          
          <p className="text-lg text-gray-600 mb-8">
            We're sorry, but your payment could not be processed at this time.
          </p>

          {/* Payment Details Card */}
          {(orderNumber || amount || reason || transactionId) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8 text-left">
              <h3 className="font-semibold text-red-800 mb-4 flex items-center">
                <AlertTriangle className="w-5 h-5 mr-2" />
                Payment Details
              </h3>
              <div className="space-y-2 text-sm">
                {orderNumber && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Order Number:</span>
                    <span className="font-medium text-gray-800">#{orderNumber}</span>
                  </div>
                )}
                {amount && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount:</span>
                    <span className="font-medium text-gray-800">৳{parseFloat(amount).toFixed(2)}</span>
                  </div>
                )}
                {transactionId && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Transaction ID:</span>
                    <span className="font-medium text-gray-800">{transactionId}</span>
                  </div>
                )}
                {reason && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Reason:</span>
                    <span className="font-medium text-red-600">{reason}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Common Reasons */}
          <div className="bg-gray-50 rounded-lg p-6 mb-8 text-left">
            <h3 className="font-semibold text-gray-800 mb-4">Common Reasons for Payment Failure:</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start">
                <span className="text-red-500 mr-2">•</span>
                Insufficient funds in your account
              </li>
              <li className="flex items-start">
                <span className="text-red-500 mr-2">•</span>
                Incorrect card details or expired card
              </li>
              <li className="flex items-start">
                <span className="text-red-500 mr-2">•</span>
                Network connectivity issues
              </li>
              <li className="flex items-start">
                <span className="text-red-500 mr-2">•</span>
                Transaction limits exceeded
              </li>
              <li className="flex items-start">
                <span className="text-red-500 mr-2">•</span>
                Bank or payment gateway maintenance
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <button
              onClick={handleRetryPayment}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center space-x-2 shadow-md hover:shadow-lg"
            >
              <RefreshCw className="w-5 h-5" />
              <span>Try Again</span>
            </button>

            <button
              onClick={handleViewOrders}
              className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center space-x-2 shadow-md hover:shadow-lg"
            >
              <ShoppingCart className="w-5 h-5" />
              <span>View Orders</span>
            </button>
          </div>

          {/* Secondary Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <button
              onClick={handleGoHome}
              className="text-gray-600 hover:text-gray-800 font-medium transition-colors duration-200 flex items-center justify-center space-x-2"
            >
              <Home className="w-4 h-4" />
              <span>Go to Homepage</span>
            </button>

            <button
              onClick={handleContactSupport}
              className="text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200 flex items-center justify-center space-x-2"
            >
              <Phone className="w-4 h-4" />
              <span>Contact Support</span>
            </button>
          </div>

          {/* Support Information */}
          <div className="border-t border-gray-200 pt-6">
            <h4 className="font-semibold text-gray-800 mb-3">Need Help?</h4>
            <div className="flex flex-col sm:flex-row gap-4 justify-center text-sm text-gray-600">
              <div className="flex items-center justify-center space-x-2">
                <Phone className="w-4 h-4" />
                <span>+8801710090044</span>
              </div>
              <div className="flex items-center justify-center space-x-2">
                <Mail className="w-4 h-4" />
                <span>support@ieducationbd.com</span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-3">
              Our support team is available 24/7 to help you with your payment issues.
            </p>
          </div>
        </div>

        {/* Security Note */}
        <div className="bg-white rounded-xl shadow-lg p-6 mt-6">
          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">Security Notice</h4>
              <p className="text-sm text-gray-600">
                For your security, this page will automatically redirect to the homepage in 5 minutes. 
                No payment has been charged to your account for this failed transaction.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PaymentFailed